/**
 * Data quality (spec §94).
 *
 * Built in phase one rather than last, because with the gaps discovery found —
 * unresolved employee ids, a third of activities unbridged, missing completion
 * times — the trust question gets asked in the first management review. Shipping
 * the answer beside the numbers is what makes the rest of the product credible.
 */
import { useQualityScorecard } from "@/hooks/useAnalytics";
import { count, pct } from "@/utils/format";

const SEVERITY_STYLE: Record<string, string> = {
  critical: "border-bad/40 bg-bad-soft text-bad",
  high: "border-warn/40 bg-warn-soft text-warn",
  medium: "border-rule-strong bg-surface-alt text-ink-soft",
  low: "border-rule bg-surface text-ink-faint",
};

export function QualityScorecard() {
  const { data, isLoading } = useQualityScorecard();

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display font-bold text-[24px] tracking-tight">Data quality</h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-2xl">
          Each finding names the features it degrades, so a missing number on
          another screen has an explanation here rather than looking like a bug.
        </p>
      </div>

      {isLoading && <div className="skeleton h-64" />}

      <div className="space-y-3">
        {(data ?? []).map((issue) => (
          <article key={issue.code} className="card p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`chip ${SEVERITY_STYLE[issue.severity] ?? ""}`}>
                    {issue.severity}
                  </span>
                  <span className="label">
                    {issue.source_table}
                    {issue.source_column ? ` · ${issue.source_column}` : ""}
                  </span>
                </div>
                <h2 className="font-display font-semibold text-[15.5px]">{issue.title}</h2>
                <p className="text-[13px] text-ink-soft mt-1.5 max-w-3xl leading-snug">
                  {issue.recommended_action}
                </p>
                {issue.blocks_features.length > 0 && (
                  <p className="text-[12px] text-ink-faint mt-2 font-mono">
                    degrades: {issue.blocks_features.join(", ")}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="font-display font-bold text-[24px] tnum">
                  {pct(issue.affected_pct)}
                </div>
                <div className="text-[12px] text-ink-muted tnum">
                  {count(issue.affected_count)} of {count(issue.total_count)}
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
